import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { Add } from '@mui/icons-material';
import api from '../../../../services/api';

const ListaDeCompras = () => {
  const [tab, setTab] = useState('categoria');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [categoria, setCategoria] = useState(null);
  const [fornecedor, setFornecedor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [fornecedorOptions, setFornecedorOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [, setLoading] = useState(false);

  const fetchCategorias = async () => {
    try {
      const { data } = await api.get('/confereai/categorias', {
        params: { page: 1, per_page: 100 },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setCategories(list);
    } catch {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchFornecedores = async (q) => {
    try {
      const { data } = await api.get('/confereai/fornecedores', {
        params: { q, per_page: 10 },
      });
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setFornecedorOptions(list);
    } catch {
      setFornecedorOptions([]);
    }
  };

  const fetchSugestoes = async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        date_to: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
      };
      if (tab === 'categoria') {
        params.categoria_id = categoria?.id || undefined;
      } else {
        params.fornecedor_id = fornecedor?.id || undefined;
      }
      const { data } = await api.get('/confereai/lista-compras/sugestoes', {
        params,
      });
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setRows(list.map((r) => ({ ...r, compra: '' })));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar sugestões');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSugestoes();
    // Omite `fetchSugestoes`; deps listam filtros que disparam o fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dateFrom, dateTo, categoria, fornecedor]);

  const handleSave = async () => {
    try {
      const itens = rows
        .filter((r) => r.compra)
        .map((r) => ({ insumo_id: r.insumo_id || r.id, quantidade: r.compra }));
      await api.post('/confereai/lista-compras', { itens });
      toast.success('Lista enviada');
    } catch {
      toast.error('Erro ao salvar lista');
    }
  };

  const handleGeneratePdf = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
    if (dateTo) params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
    if (tab === 'categoria' && categoria) params.append('categoria_id', categoria.id);
    if (tab === 'fornecedor' && fornecedor) params.append('fornecedor_id', fornecedor.id);
    window.open(`/confereai/lista-compras/export/pdf?${params.toString()}`, '_blank');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary">
        Lista de Compras
      </Typography>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        indicatorColor="primary"
      >
        <Tab label="Por Categoria" value="categoria" />
        <Tab label="Por Fornecedor" value="fornecedor" />
      </Tabs>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={3}>
          <DatePicker
            selected={dateFrom}
            onChange={(d) => setDateFrom(d)}
            customInput={<TextField fullWidth label="Data Inicial" />}
            dateFormat="dd/MM/yyyy"
            locale={pt}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <DatePicker
            selected={dateTo}
            onChange={(d) => setDateTo(d)}
            customInput={<TextField fullWidth label="Data Final" />}
            dateFormat="dd/MM/yyyy"
            locale={pt}
          />
        </Grid>
        {tab === 'categoria' ? (
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoria?.id || ''}
                label="Categoria"
                onChange={(e) => {
                  const cat = categories.find((c) => c.id === e.target.value) || null;
                  setCategoria(cat);
                }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : (
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={fornecedorOptions}
              getOptionLabel={(o) => o.nome || ''}
              onInputChange={(e, v) => fetchFornecedores(v)}
              value={fornecedor}
              onChange={(e, v) => setFornecedor(v)}
              renderInput={(params) => (
                <TextField {...params} label="Fornecedor" fullWidth />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={3} textAlign={{ xs: 'left', sm: 'right' }}>
          <Button variant="outlined" sx={{ mr: 1 }} onClick={handleGeneratePdf}>
            Gerar PDF
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleSave}
          >
            Salvar Lista
          </Button>
        </Grid>
      </Grid>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Custo Médio</TableCell>
            <TableCell>Últ. Custo</TableCell>
            <TableCell>Saídas</TableCell>
            <TableCell>Estoque Final</TableCell>
            <TableCell>Sugestão</TableCell>
            <TableCell>Compra</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.insumo_id || row.id}>
              <TableCell>{row.item_nome || row.nome}</TableCell>
              <TableCell>{row.custo_medio}</TableCell>
              <TableCell>{row.ultimo_custo}</TableCell>
              <TableCell>{row.saidas}</TableCell>
              <TableCell>{row.estoque_final}</TableCell>
              <TableCell>{row.sugestao}</TableCell>
              <TableCell>
                <TextField
                  type="number"
                  value={row.compra}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRows((rs) =>
                      rs.map((r) =>
                        r === row ? { ...r, compra: val } : r
                      )
                    );
                  }}
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                Nenhum item
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ListaDeCompras;
